import { useMemo } from 'react';

import produce from 'immer';
import { create } from 'zustand';

import {
  DataApiClient,
  type DataIntegrityValidationResult,
  type InvalidHyperNodeContext,
  type ProjectNode,
  type RepairNodeContext,
  type RepairRelationContext,
  type RepairRelationResult
} from 'api/data/client-dataapi';
import { type OutputOperationResult } from 'api/types';

export type ProjectBlockingOperation = {
  projectId: string;
  message: string;
};
export type ProjectOperationProgress = {
  ProgressPercent: number;
  Message: string;
  EventId: string;
  IsBlocked?: boolean;
};

export type ProjectUpdateConflict = {
  message: string;
};

export type ProjectsState = {
  projects: ProjectNode[];
  projectsLoaded: boolean;
  projectsLoadError: boolean;

  setProjects: (projects: ProjectNode[]) => void;
  setProjectsLoadError: (hasError: boolean) => void;
  setProjectsLoading: () => void;

  addProject: (project: ProjectNode) => void;
  removeProject: (projectId: string) => void;
  updateProject: (project: ProjectNode) => void;
  patchProject: (projectId: string, patch: Partial<ProjectNode>) => void;

  projectBlockingOperations: { [eventId: string]: ProjectBlockingOperation };
  projectOperationProgress: { [eventId: string]: ProjectOperationProgress };
  projectBlockingLogs: { [eventId: string]: string[] };
  // Conflicts that blocked an update. Latched here (keyed by projectId) so they outlive the
  // blocking operation, which is removed by the trailing ProjectUpdated event.
  projectUpdateConflicts: { [projectId: string]: ProjectUpdateConflict };

  addBlockingOperation: (projectId: string, eventId: string, message: string) => void;
  updateOperationProgress: (progress: ProjectOperationProgress) => void;
  removeBlockingOperation: (eventId: string) => void;
  clearUpdateConflict: (projectId: string) => void;
};

export const PROJECTS_STORE = create<ProjectsState>((set) => ({
  projects: [],
  projectsLoaded: false,
  projectsLoadError: false,
  setProjects: (projects: ProjectNode[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projects.splice(0);
        draft.projects.push(...projects);
        draft.projectsLoaded = true;
        draft.projectsLoadError = false;
      });

      return newState;
    }),
  setProjectsLoadError: (hasError: boolean) => set({ projectsLoadError: hasError }),
  setProjectsLoading: () => set({ projectsLoaded: false, projectsLoadError: false }),

  addProject: (project: ProjectNode) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projects.push(project);
      });

      return newState;
    }),

  removeProject: (projectId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const projectIndex = state.projects.findIndex((p) => p.id === projectId);
        if (projectIndex !== -1) {
          draft.projects.splice(projectIndex, 1);
        }
      });

      return newState;
    }),

  updateProject: (project: ProjectNode) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const projectIndex = state.projects.findIndex((p) => p.id === project.id);
        const stateProject = state.projects[projectIndex];
        if (!stateProject) {
          return;
        }

        draft.projects[projectIndex] = { ...project, name: stateProject.name };
      });

      return newState;
    }),
  patchProject: (projectId: string, patch: Partial<ProjectNode>) =>
    set(({ projects }) => {
      return {
        projects: projects.map((project) => {
          return project.id === projectId ? { ...project, ...patch } : project;
        })
      };
    }),
  projectBlockingOperations: {},
  projectBlockingLogs: {},
  projectOperationProgress: {},
  projectUpdateConflicts: {},
  addBlockingOperation: (projectId: string, eventId: string, message: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectBlockingOperations[eventId] = { projectId: projectId, message: message };
      });

      return newState;
    }),

  updateOperationProgress: (progress: ProjectOperationProgress) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectOperationProgress[progress.EventId] = progress;

        const draftProjectBlockingLogs = draft.projectBlockingLogs[progress.EventId];
        if (draftProjectBlockingLogs) {
          draftProjectBlockingLogs.push(progress.Message);
        } else {
          draft.projectBlockingLogs[progress.EventId] = [progress.Message];
        }

        // A blocked update (e.g. a cardinality conflict) is aborted server-side; latch the
        // conflict by projectId so the UI can surface it after the block itself is removed.
        if (progress.IsBlocked) {
          const projectId = draft.projectBlockingOperations[progress.EventId]?.projectId;
          if (projectId) {
            draft.projectUpdateConflicts[projectId] = { message: progress.Message };
          }
        }
      });

      return newState;
    }),

  removeBlockingOperation: (eventId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        delete draft.projectBlockingOperations[eventId];
        delete draft.projectOperationProgress[eventId];
        delete draft.projectBlockingLogs[eventId];
      });

      return newState;
    }),

  clearUpdateConflict: (projectId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        delete draft.projectUpdateConflicts[projectId];
      });

      return newState;
    })
}));

export const useProjectActions = () => {
  return useMemo(() => {
    return new ProjectActions();
  }, []);
};

export class ProjectActions {
  private dataClient: DataApiClient;

  constructor() {
    this.dataClient = new DataApiClient();
  }

  async GetAll() {
    const store = PROJECTS_STORE.getState();
    store.setProjectsLoading();
    try {
      const result = await this.dataClient.GetProjects();
      if (result.success) {
        store.setProjects(result.result);
      } else {
        store.setProjectsLoadError(true);
        console.error(result.error);
      }
    } catch (error) {
      store.setProjectsLoadError(true);
      console.error(error);
    }
  }

  async Create({
    name,
    blueprintId,
    nrTestNodeInstances,
    teamId,
    rolesPerUser
  }: {
    name: string;
    blueprintId: string;
    nrTestNodeInstances: number;
    teamId?: string;
    rolesPerUser: { [email: string]: string[] };
  }) {
    const result = await this.dataClient.CreateProject({
      templateId: blueprintId,
      name,
      numberOfTestDataNodes: nrTestNodeInstances,
      projectTeamId: teamId,
      userRoleAssignments: rolesPerUser
    });

    if (result.success) {
      PROJECTS_STORE.getState().addProject(result.result);
    }

    return result;
  }

  async UnlockProjectFromCache(projectId: string) {
    await this.dataClient.UnlockProjectFromCache(projectId);
  }

  // data integrity
  async ValidateProjectData(projectId: string): Promise<OutputOperationResult<DataIntegrityValidationResult>> {
    return await this.dataClient.ValidateProjectData(projectId);
  }

  async ValidateHyperNodeStructures(projectId: string): Promise<OutputOperationResult<InvalidHyperNodeContext[]>> {
    return await this.dataClient.ValidateHyperNodeStructures(projectId);
  }

  async RepairHyperNodeStructures(projectId: string): Promise<OutputOperationResult<unknown>> {
    return await this.dataClient.RepairHyperNodeStructures(projectId);
  }

  async RelabelRelations(
    projectId: string,
    context: RepairRelationContext
  ): Promise<OutputOperationResult<RepairRelationResult>> {
    return await this.dataClient.RelabelRelations(projectId, context);
  }

  async RelabelNodes(
    projectId: string,
    context: RepairNodeContext
  ): Promise<OutputOperationResult<RepairRelationResult>> {
    return await this.dataClient.RelabelNodes(projectId, context);
  }
}
